import { stableRecordKey } from '../../lib/rowIdentity';
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Loader2, CalendarDays, Settings2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../../App';

/* Saturday Club settings (spec P4.1): terms/sessions, ages, capacity, notices, contact, fees.
   Nothing is pre-filled with invented dates - admins enter the real term dates. */
export function SaturdayClubSettingsTab({ token, role }) {
  const canEdit = ['admin', 'chairman'].includes(role);
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [terms, setTerms] = useState([]);

  const load = () => api.getAuth('/api/admin/saturday-club/settings', token).then(d => { setS(d); setTerms(d.terms || []); }).catch(e => toast.error(e.message));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const setTerm = (i, k, v) => setTerms(prev => prev.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  const addTerm = () => setTerms(prev => [...prev, { name: '', start: '', end: '', half_term: '', notes: '' }]);
  const removeTerm = (i) => setTerms(prev => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!s) return;
    for (const t of terms) {
      if (!t.name.trim()) { toast.error('Every term needs a name'); return; }
      if (t.start && t.end && t.end < t.start) { toast.error(`${t.name}: end date is before start date`); return; }
    }
    setSaving(true);
    try {
      const body = {
        registrations_open: !!s.registrations_open,
        closed_message: s.closed_message || '',
        session_info: s.session_info || '',
        venue: s.venue || '',
        notice: s.notice || '',
        contact_email: s.contact_email || '',
        contact_phone: s.contact_phone || '',
        allow_custom: !!s.allow_custom,
        show_terms: s.show_terms !== false,
        price_term: Number(s.prices?.term),
        price_yearly: Number(s.prices?.yearly),
        terms: terms.map(t => ({ term_id: t.term_id, name: t.name.trim(), start: t.start || null, end: t.end || null, half_term: t.half_term || '', notes: t.notes || '' })),
      };
      if (s.age_min === '' || s.age_min == null) { if (s.age_max === '' || s.age_max == null) body.clear_age_limits = true; }
      if (s.age_min !== '' && s.age_min != null) body.age_min = Number(s.age_min);
      if (s.age_max !== '' && s.age_max != null) body.age_max = Number(s.age_max);
      if (s.capacity === '' || s.capacity == null) body.clear_capacity = true; else body.capacity = Number(s.capacity);
      const d = await api.putAuth('/api/admin/saturday-club/settings', body, token);
      setS(prev => ({ ...prev, ...d })); setTerms(d.terms || []);
      toast.success('Saturday Club settings saved — the public page is updated');
    } catch (e) { toast.error(e.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="animate-pulse h-64 bg-[hsl(var(--muted))] rounded-xl" />;

  return (
    <div className="space-y-6" data-testid="saturday-club-settings-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Saturday Club Settings</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Controls the public Saturday Club page, registration rules and fee options. {s.paid_count != null && <>Paid registrations: <strong>{s.paid_count}</strong>{s.capacity ? ` / ${s.capacity}` : ''}.</>}</p>
        </div>
        {canEdit && <Button onClick={save} disabled={saving} data-testid="saturday-club-settings-save"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save changes'}</Button>}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Settings2 className="w-4 h-4" /> Registration & sessions</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <div className="font-medium text-sm">Registrations open</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">When off, the public form is hidden and the message below is shown.</div>
              </div>
              <Switch checked={!!s.registrations_open} onCheckedChange={v => set('registrations_open', v)} disabled={!canEdit} data-testid="saturday-club-open-switch" />
            </div>
            <div>
              <Label htmlFor="sc-closed">Closed message</Label>
              <Input id="sc-closed" value={s.closed_message || ''} onChange={e => set('closed_message', e.target.value)} disabled={!canEdit} data-testid="saturday-club-closed-message" />
            </div>
            <div>
              <Label htmlFor="sc-session">Session info (public "When")</Label>
              <Input id="sc-session" value={s.session_info || ''} onChange={e => set('session_info', e.target.value)} placeholder="e.g. Every Saturday 10:00 - 12:30" disabled={!canEdit} data-testid="saturday-club-session-info-input" />
            </div>
            <div>
              <Label htmlFor="sc-venue">Venue (public "Where")</Label>
              <Input id="sc-venue" value={s.venue || ''} onChange={e => set('venue', e.target.value)} disabled={!canEdit} data-testid="saturday-club-venue-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="sc-age-min">Min age</Label>
                <Input id="sc-age-min" type="number" min="0" value={s.age_min ?? ''} onChange={e => set('age_min', e.target.value)} placeholder="—" disabled={!canEdit} data-testid="saturday-club-age-min" />
              </div>
              <div>
                <Label htmlFor="sc-age-max">Max age</Label>
                <Input id="sc-age-max" type="number" min="0" value={s.age_max ?? ''} onChange={e => set('age_max', e.target.value)} placeholder="—" disabled={!canEdit} data-testid="saturday-club-age-max" />
              </div>
              <div>
                <Label htmlFor="sc-cap">Capacity</Label>
                <Input id="sc-cap" type="number" min="0" value={s.capacity ?? ''} onChange={e => set('capacity', e.target.value)} placeholder="unlimited" disabled={!canEdit} data-testid="saturday-club-capacity" />
              </div>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Leave age/capacity blank for no limit. Ages are checked when a parent registers; when paid registrations reach capacity the public form shows "full".</p>
            <div>
              <Label htmlFor="sc-notice">Public notice banner (optional)</Label>
              <Textarea id="sc-notice" rows={2} value={s.notice || ''} onChange={e => set('notice', e.target.value)} placeholder="e.g. No session on 26 October (half term)." disabled={!canEdit} data-testid="saturday-club-notice-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="sc-cemail">Club contact email</Label>
                <Input id="sc-cemail" type="email" value={s.contact_email || ''} onChange={e => set('contact_email', e.target.value)} disabled={!canEdit} data-testid="saturday-club-contact-email" />
              </div>
              <div>
                <Label htmlFor="sc-cphone">Club contact phone</Label>
                <Input id="sc-cphone" type="tel" value={s.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)} disabled={!canEdit} data-testid="saturday-club-contact-phone" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Fees (charged via Stripe)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="sc-p-term">Per child, per term (£)</Label>
                  <Input id="sc-p-term" type="number" min="0.5" step="0.5" value={s.prices?.term ?? ''} onChange={e => set('prices', { ...s.prices, term: e.target.value })} disabled={!canEdit} data-testid="saturday-club-price-term" />
                </div>
                <div>
                  <Label htmlFor="sc-p-year">Per child, per year (£)</Label>
                  <Input id="sc-p-year" type="number" min="0.5" step="0.5" value={s.prices?.yearly ?? ''} onChange={e => set('prices', { ...s.prices, yearly: e.target.value })} disabled={!canEdit} data-testid="saturday-club-price-yearly" />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <div className="font-medium text-sm">Allow custom amount</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">Parents may enter their own amount (min £0.50).</div>
                </div>
                <Switch checked={!!s.allow_custom} onCheckedChange={v => set('allow_custom', v)} disabled={!canEdit} data-testid="saturday-club-allow-custom-switch" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Term dates</span>
                <div className="flex items-center gap-3 text-xs font-normal">
                  <span className="flex items-center gap-2">Show on public page <Switch checked={s.show_terms !== false} onCheckedChange={v => set('show_terms', v)} disabled={!canEdit} data-testid="saturday-club-show-terms-switch" /></span>
                  {canEdit && <Button size="sm" variant="outline" onClick={addTerm} data-testid="saturday-club-add-term"><Plus className="w-3.5 h-3.5 mr-1" /> Add term</Button>}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {terms.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="saturday-club-no-terms">No terms configured yet — the public page shows "dates to be announced". Add the real term dates here.</p>}
              {terms.map((t, i) => (
                <div key={stableRecordKey(t)} className="p-3 rounded-lg border space-y-2" data-testid={`saturday-club-term-row-${i}`}>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label>Term name</Label>
                      <Input value={t.name} onChange={e => setTerm(i, 'name', e.target.value)} placeholder="e.g. Autumn Term 2026" disabled={!canEdit} data-testid={`saturday-club-term-name-${i}`} />
                    </div>
                    {canEdit && <Button variant="ghost" size="icon" onClick={() => removeTerm(i)} aria-label="Remove term" data-testid={`saturday-club-term-remove-${i}`}><Trash2 className="w-4 h-4 text-[hsl(var(--destructive))]" /></Button>}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Start</Label><Input type="date" value={t.start || ''} onChange={e => setTerm(i, 'start', e.target.value)} disabled={!canEdit} data-testid={`saturday-club-term-start-${i}`} /></div>
                    <div><Label>End</Label><Input type="date" value={t.end || ''} onChange={e => setTerm(i, 'end', e.target.value)} disabled={!canEdit} data-testid={`saturday-club-term-end-${i}`} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Half term (text)</Label><Input value={t.half_term || ''} onChange={e => setTerm(i, 'half_term', e.target.value)} placeholder="e.g. 26 - 30 Oct" disabled={!canEdit} /></div>
                    <div><Label>Notes</Label><Input value={t.notes || ''} onChange={e => setTerm(i, 'notes', e.target.value)} placeholder="optional" disabled={!canEdit} /></div>
                  </div>
                </div>
              ))}
              {terms.length > 0 && <p className="text-xs text-[hsl(var(--muted-foreground))]">Current and upcoming terms are offered when a parent pays "per term"; finished terms are greyed out publicly.</p>}
            </CardContent>
          </Card>
        </div>
      </div>
      {!canEdit && <Badge variant="outline">Read-only for your role</Badge>}
    </div>
  );
}
